import { endOfDay } from 'date-fns';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { CanReceiptUseNegativeQty, InvoiceRequireReceiptDate } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import { validateDateFormat } from 'src/utils/formatTime';

const endOfDate = moment().endOf('day').format();
const dateToday = new Date(endOfDate);
const twoYearsBackDate = new Date(moment().subtract(2, 'years').format());

const useInvoiceErrors = (invoice: InvoiceDetailsResponse, isCreditMemo: boolean) => {
  const CanReceiptUseNegativeQtyPermission = usePermission({
    permissions: CanReceiptUseNegativeQty,
  });

  const isInvoiceRequireReceiptDate = usePermission({ permissions: InvoiceRequireReceiptDate });

  const receivedDate = useMemo(() => {
    let errors: string[] = [];

    if (isInvoiceRequireReceiptDate && !invoice?.receivedDate?.toString().trim()) {
      errors = ['Received Date is required.'];
    }
    if (invoice?.receivedDate && new Date(invoice?.receivedDate) > endOfDay(new Date())) {
      errors = ['Received Date cannot be in the future'];
    } else if (!validateDateFormat(invoice?._receivedDate)) {
      errors = ['Received Date format is not valid'];
    }
    return errors;
  }, [invoice?._receivedDate, invoice?.receivedDate, isInvoiceRequireReceiptDate]);

  const invoiceDate = useMemo(() => {
    let errors: string[] = [];
    if (!invoice?.invoiceDate) {
      errors = ['Invoice/Credit Date is required.'];
    } else if (!validateDateFormat(invoice?._invoiceDate)) {
      errors = ['Invoice/Credit Date format is not valid'];
    } else if (invoice?.invoiceDate > dateToday) {
      errors = ['Invoice/Credit Date cannot be in future'];
    } else if (invoice?.invoiceDate < twoYearsBackDate) {
      errors = ['Invoice/Credit Date cannot be more than 2 years in past'];
    }
    return errors;
  }, [invoice?._invoiceDate, invoice?.invoiceDate]);

  const discountAmt = useMemo(() => {
    let errors: string[] = [];

    if (
      (invoice?.discountAmt !== undefined &&
        !invoice?.discountAmt &&
        Number(invoice?.discountAmt) !== 0) ||
      invoice?.discountAmt?.toString().trim() === '' ||
      isNaN(invoice?.discountAmt as number)
    ) {
      errors = ['Discount Amount is required.'];
    }

    if (isCreditMemo && !((Number(invoice.discountAmt) || 0) <= 0)) {
      errors = [...errors, 'Credit memo should have negative discount.'];
    } else if (!isCreditMemo && !((Number(invoice.discountAmt) || 0) >= 0)) {
      errors = [...errors, 'Invoice should have positive discount.'];
    }

    return errors;
  }, [invoice.discountAmt, isCreditMemo]);

  const salesTaxAmt = useMemo(() => {
    let errors: string[] = [];
    if (
      (invoice?.salesTaxAmt !== undefined &&
        !invoice?.salesTaxAmt &&
        Number(invoice?.salesTaxAmt) !== 0) ||
      invoice?.salesTaxAmt?.toString().trim() === '' ||
      isNaN(invoice?.salesTaxAmt as number)
    )
      errors = ['Tax Amount is required.'];

    if (isCreditMemo && !((Number(invoice.salesTaxAmt) || 0) <= 0)) {
      errors = [...errors, 'Credit memo should have negative sales tax.'];
    } else if (!isCreditMemo && !((Number(invoice.salesTaxAmt) || 0) >= 0)) {
      errors = [...errors, 'Invoice should have positive sales tax.'];
    }

    return errors;
  }, [invoice.salesTaxAmt, isCreditMemo]);

  const shipCostAmt = useMemo(() => {
    let errors: string[] = [];
    if (
      (invoice?.shipCostAmt !== undefined &&
        !invoice?.shipCostAmt &&
        Number(invoice?.shipCostAmt) !== 0) ||
      invoice?.shipCostAmt?.toString().trim() === '' ||
      isNaN(invoice?.shipCostAmt as number)
    )
      errors = ['Shipping is required.'];

    if (isCreditMemo && !((Number(invoice.shipCostAmt) || 0) <= 0)) {
      errors = [...errors, 'Credit memo should have negative shipping.'];
    } else if (!isCreditMemo && !((Number(invoice.shipCostAmt) || 0) >= 0)) {
      errors = [...errors, 'Invoice should have positive shipping.'];
    }

    return errors;
  }, [invoice.shipCostAmt, isCreditMemo]);

  const totalCostAmt = useMemo(() => {
    let errors: string[] = [];

    if (
      (invoice?.totalCostAmt !== undefined &&
        !invoice?.totalCostAmt &&
        Number(invoice?.totalCostAmt) !== 0) ||
      invoice?.totalCostAmt?.toString().trim() === '' ||
      isNaN(invoice?.totalCostAmt as number)
    )
      errors = ['Grand Total is required.'];

    if (
      isCreditMemo &&
      invoice?.totalCostAmt !== undefined &&
      Number(invoice?.totalCostAmt) < 0 &&
      !CanReceiptUseNegativeQtyPermission
    ) {
      errors = [...errors, 'You do not have permission to have negative total.'];
    }
    if (isCreditMemo && !((Number(invoice.totalCostAmt) || 0) <= 0)) {
      errors = [...errors, 'Credit memo should have negative total.'];
    } else if (!isCreditMemo && !((Number(invoice.totalCostAmt) || 0) >= 0)) {
      errors = [...errors, 'Invoice should have positive total.'];
    }

    return errors;
  }, [CanReceiptUseNegativeQtyPermission, invoice.totalCostAmt, isCreditMemo]);

  const notes = useMemo(
    () =>
      String(invoice?.notes).trim().length > 255 ? ['Notes are more than 255 characters.'] : [],
    [invoice?.notes]
  );

  return { receivedDate, discountAmt, salesTaxAmt, shipCostAmt, totalCostAmt, invoiceDate, notes };
};
export default useInvoiceErrors;
